import { motion, AnimatePresence } from 'framer-motion'
import { BaseSyntheticEvent, useContext, useState } from 'react'
import { useForm } from 'react-hook-form'

import { SanityAuthStrings } from '@data/sanity/queries/types/strings'
import { ParseStatus } from '@lib/shopify/graphql/client'
import { recoverUserPassword } from '@lib/shopify/graphql/customer'
import { fadeAnimation } from '@lib/animate'
import { ErrorMessages } from '@lib/helpers'
import { ShopContext } from '@lib/shop-context'
import { StringsContext } from '@lib/strings-context'
import { PasswordRecoveryFormValues } from '@lib/user/types'

import Alert from '@components/alert'
import Button from '@components/buttons/button'
import InputField from '@components/input-field/with-form-register'
import ComplexPortableText from '@components/portable-text/complex'

interface PasswordRecoveryFormProps {
  authStrings: SanityAuthStrings
  className?: string
}

const PasswordRecoveryForm = ({
  authStrings,
  className,
}: PasswordRecoveryFormProps) => {
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<PasswordRecoveryFormValues>()
  const strings = useContext(StringsContext)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({})
  const [isLoading, setIsLoading] = useState(false)
  const { shopifyStorefrontClient } = useContext(ShopContext)

  // Handle form submission
  const onSubmit = async (
    values: PasswordRecoveryFormValues,
    event?: BaseSyntheticEvent,
  ) => {
    event?.preventDefault()

    if (!shopifyStorefrontClient) {
      throw new Error('Shopify Storefront API client missing')
    }

    setIsLoading(true)
    setIsError(false)

    const recoverUserPasswordResult = await recoverUserPassword(
      shopifyStorefrontClient,
      values.email,
    )
    setErrorMessages(recoverUserPasswordResult.fieldErrors)

    if (recoverUserPasswordResult.status !== ParseStatus.OK) {
      setIsError(true)
    }

    if (
      recoverUserPasswordResult.status === ParseStatus.OK &&
      recoverUserPasswordResult.errorCount === 0
    ) {
      reset()
      setIsSuccess(true)
    }

    setIsLoading(false)
  }

  const emailRegister = register('email', {
    required: authStrings.signupEmailMissing,
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      message: strings.emailInvalid,
    },
  })

  const isDisabled = !!errors.email

  return (
    <form className={className} onSubmit={handleSubmit(onSubmit)}>
      <AnimatePresence mode="wait">
        {isSuccess && (
          <motion.div
            key="success"
            initial="hide"
            animate="show"
            exit="hide"
            variants={fadeAnimation}
          >
            <Alert success>
              <ComplexPortableText
                content={authStrings.passwordRecoverySuccessMessage}
                className="rc rc-success"
              />
            </Alert>
          </motion.div>
        )}

        {!isSuccess && (
          <motion.div
            key="form"
            initial="hide"
            animate="show"
            exit="hide"
            variants={fadeAnimation}
          >
            <InputField
              type="email"
              autoComplete="email"
              formRegister={emailRegister}
              errorMessage={errorMessages?.email ?? errors.email?.message}
              label={authStrings.signupEmail}
              placeholder={authStrings.signupEmailPlaceholder}
            />

            {isError && (
              <div key="error" className="mt-8">
                <Alert error>
                  <ComplexPortableText
                    content={authStrings.passwordRecoveryErrorMessage}
                    className="rc rc-error"
                  />
                </Alert>
              </div>
            )}

            <div className="flex justify-center mt-10">
              <Button
                type="submit"
                variant="primary"
                color="red"
                disabled={isLoading || isDisabled}
                className="min-w-[260px]"
              >
                {isLoading
                  ? strings.buttonSubmitting
                  : authStrings.passwordRecoverySubmit}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  )
}

export default PasswordRecoveryForm
